import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import VideoCard from './VideoCard';
import VideoModal from './VideoModal';
import { tokens } from '../design/tokens';
import { videoService } from '../services/video.service'; // Usa el servicio
import { Video } from '../types'; // Importa el tipo centralizado

const Container = styled.div`
  width: 100%;
  margin: 0;
  padding: 0;
  overflow-x: hidden;
`;

const Header = styled.div`
  background: rgba(255, 255, 255, 0.95);
  border-radius: 20px;
  padding: 30px;
  margin-bottom: 30px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  text-align: center;
`;

const LogoContainer = styled.div`
  margin-bottom: 20px;
`;

const Logo = styled.img`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  object-fit: cover;
  border: 4px solid ${tokens.colors.primary};
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
`;

const SiteTitle = styled.h1`
  font-size: 42px;
  font-weight: bold;
  color: ${tokens.colors.primary};
  margin: 15px 0 10px 0;

  @media (max-width: 768px) {
    font-size: 32px;
  }
`;

const WelcomeText = styled.p`
  font-size: 18px;
  color: #555;
  margin: 0;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 40px;
`;

const SectionTitle = styled.h2`
  font-size: 36px;
  font-weight: bold;
  color: white;
  margin: 0 0 10px 0;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.2);

  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

const FilterContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 15px;
  margin-bottom: 30px;
  flex-wrap: wrap;
`;

const FilterButton = styled.button<{ $active: boolean }>`
  background: ${props => props.$active ? tokens.colors.primary : 'rgba(255, 255, 255, 0.9)'};
  color: ${props => props.$active ? tokens.colors.textOnDark : tokens.colors.primary};
  border: 2px solid ${tokens.colors.primary};
  padding: 10px 20px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: ${tokens.colors.primary};
    color: ${tokens.colors.textOnDark};
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 25px;
  margin-bottom: 30px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  color: white;
  font-size: 18px;
  padding: 40px;
`;

const ErrorMessage = styled.div`
  text-align: center;
  color: #ff6b6b;
  font-size: 18px;
  padding: 40px;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 15px;
  margin: 20px 0;
`;

const VideoList: React.FC = () => {
  const [videos, setVideos] = useState<Video[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedVideo, setSelectedVideo] = useState<Video | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState<string>('todos');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [levels, setLevels] = useState<string[]>([]);
  const observer = useRef<IntersectionObserver | null>(null);

  const lastVideoElementRef = useCallback((node: HTMLDivElement) => {
    if (loadingMore) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        setPage(prevPage => prevPage + 1);
      }
    });
    if (node) observer.current.observe(node);
  }, [loadingMore, hasMore]);

  const fetchVideos = useCallback(async (isLoadMore = false) => {
    try {
      if (isLoadMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        setError(null);
        if (page === 1) setHasMore(true);
      }

      const limit = isLoadMore ? 10 : 20;

      const newVideos = await videoService.getAll({
        level: categoryFilter,
        page: page,
        limit: limit
      });

      if (isLoadMore) {
        setVideos(prev => [...prev, ...newVideos]);
        setHasMore(newVideos.length === limit);
      } else {
        setVideos(newVideos);
        setHasMore(newVideos.length === limit);
      }
    } catch (err) {
      setError('Error al cargar videos. Inténtalo de nuevo.');
      console.error('Error fetching videos:', err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [categoryFilter, page]);

  useEffect(() => {
    const loadLevels = async () => {
      try {
        const data = await videoService.getLevels();
        setLevels(data || []);
      } catch {
        setLevels([]);
      }
    };
    loadLevels();
  }, []);

  useEffect(() => {
    fetchVideos(page > 1);
  }, [fetchVideos, page]);

  const handleViewVideo = (video: Video) => {
    setSelectedVideo(video);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedVideo(null);
  };

  const handleCategoryFilter = (category: string) => {
    setCategoryFilter(category);
    setPage(1);
    setHasMore(true);
  };

  return (
    <Container>
      <Header>
        <LogoContainer>
          <Logo
            src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTuEgWEx7V8bkTxxJW7nsENV9-ffZJwwIsSUQ&s"
            alt="Logo"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
        </LogoContainer>
        <SiteTitle>Café Tutoriales</SiteTitle>
        <WelcomeText>Aprende con nuestros tutoriales paso a paso</WelcomeText>
      </Header>

      <SectionHeader>
        <SectionTitle>Explora Nuestros Tutoriales</SectionTitle>
      </SectionHeader>

      <FilterContainer>
        {['todos', ...levels].map((level) => (
          <FilterButton
            key={level}
            $active={categoryFilter === level}
            onClick={() => handleCategoryFilter(level)}
          >
            {level === 'todos' ? 'Todos' : level}
          </FilterButton>
        ))}
      </FilterContainer>

      {loading && <LoadingMessage>Cargando videos...</LoadingMessage>}

      {error && <ErrorMessage>{error}</ErrorMessage>}

      {!loading && !error && (
        <Grid>
          {videos.map((video, index) => (
            <VideoCard
              key={video.id}
              video={video}
              onViewVideo={handleViewVideo}
              ref={index === videos.length - 1 ? lastVideoElementRef : undefined}
            />
          ))}
        </Grid>
      )}

      {loadingMore && <LoadingMessage>Cargando más videos...</LoadingMessage>}

      <VideoModal
        video={selectedVideo}
        isOpen={modalOpen}
        onClose={handleCloseModal}
      />
    </Container>
  );
};

export default VideoList;